// Live design reference — primitives, tokens and conventions.
//
// The body of this page is deliberately HARDCODED ENGLISH, exactly like its
// sibling `patterns.tsx`: it is developer documentation rendered as a page,
// not product copy. Nobody outside the team reads it, its vocabulary is the
// vocabulary of the codebase (`--muted-foreground`, `size="icon"`,
// `data-testid`), and translating it would mean maintaining a second copy of
// the engineering rules in a language no rule is written in. So: no i18n keys
// here, and none should be added.
//
// Split of responsibilities with `/$org/patterns`, written into both
// overviews so nobody adds a section to the wrong page:
//   design-reference = WHAT EXISTS  — tokens, primitives, conventions.
//   patterns         = HOW WE COMPOSE IT — production components fed static
//                      data, one snapshot per section.
// A composed pattern never moves here; a primitive never gets a patterns
// section.
//
// Unlinked from the sidebar — discoverable by URL, like `/patterns` and
// `/debug`. Verified by `front/e2e/design-reference.spec.ts`.

import { useRef, useState } from 'react'
import { VoteBar } from '../../components/assemblies/VoteBar'
import { HelpHint } from '../../components/assemblies/HelpHint'
import { Link, createFileRoute, useParams } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
    AppIcon,
    APP_ICON_SIZES,
    APP_ICON_TONE_CLASSES,
    type AppIconSize,
    type AppIconTone,
} from '../../components/ui/icon'
import {
    IconTile,
    ICON_TILE_GRADIENTS,
    ICON_TILE_SIZES,
    type IconTileGradient,
    type IconTileSize,
} from '../../components/ui/icon-tile'
import {
    AssetTypeTile,
    assetTypeGradient,
    type AssetTypeLike,
} from '../../components/assets/AssetTypeTile'
import { Logomark } from '../../components/Logomark'
import { ArchivedMarker, AssetName } from '../../components/ArchivedMarker'
import { mdiCheck, mdiContentCopy, mdiDelete, mdiMagnify, mdiPencil, mdiTag } from '@mdi/js'

import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useTheme } from '../../contexts/ThemeContext'
import { PageHeader } from '../../components/shared/PageHeader'
import { ENTITY_ICON_KEYS, SECTION_ICONS, type SectionIconKey } from '../../lib/section-icons'
import { AttentionBadge, AttentionDot } from '../../components/ui/attention-badge'
import { Button } from '../../components/ui/button'
import { AddIconButton } from '../../components/ui/add-icon-button'
import { Badge, badgeVariants } from '../../components/ui/badge'
import { TagBadgeLink } from '../../components/tags/TagBadgeLink'
import { TagPicker } from '../../components/tags/TagPicker'
import type { Tag } from '../../api/tags'
import { cn } from '../../lib/utils'
import { Input } from '../../components/ui/input'
import { Textarea } from '../../components/ui/textarea'
import { Label } from '../../components/ui/label'
import { Checkbox } from '../../components/ui/checkbox'
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group'
import { FieldRow } from '../../components/ui/field-row'
import { NullableDateField } from '../../components/ui/NullableDateField'
import { EditableValue } from '../../components/ui/editable-value'
import { MultilingualInput } from '../../components/ui/multilingual-input'
import { Skeleton } from '../../components/ui/skeleton'
import { EmptyState } from '../../components/ui/empty-state'
import { Separator } from '../../components/ui/separator'
import { ScrollArea } from '../../components/ui/scroll-area'
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert'
import { DeleteConfirmDialog } from '../../components/ui/delete-confirm-dialog'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select'
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../../components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../../components/ui/dialog'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '../../components/ui/alert-dialog'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../../components/ui/tooltip'
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu'
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from '../../components/ui/sheet'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '../../components/ui/command'
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '../../components/ui/form'

// ---------------------------------------------------------------------------
// Page furniture
// ---------------------------------------------------------------------------

// Editable value: the resting, hover (live, move the mouse), editing and empty
// states side by side. Escape cancels, Enter and blur save.
function EditableValueDemo() {
    const [value, setValue] = useState('Boiler maintenance')
    const [draft, setDraft] = useState(value)
    const [editing, setEditing] = useState(false)
    const settled = useRef(false)
    const save = () => {
        if (settled.current) return
        settled.current = true
        setValue(draft.trim())
        setEditing(false)
    }
    const cancel = () => {
        settled.current = true
        setEditing(false)
    }
    return (
        <div className="grid max-w-xl gap-4 sm:grid-cols-2" data-testid="design-editable-value">
            <div>
                <p className="mb-1 text-xs text-muted-foreground">Resting / hover / editing (click it)</p>
                {editing ? (
                    <Input
                        autoFocus
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={save}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') save()
                            if (e.key === 'Escape') cancel()
                        }}
                        data-testid="design-editable-value-input"
                    />
                ) : (
                    <EditableValue
                        canEdit
                        onEdit={() => { settled.current = false; setDraft(value); setEditing(true) }}
                        title="Click to edit"
                        placeholder="Click to edit"
                        data-testid="design-editable-value-text"
                    >
                        {value}
                    </EditableValue>
                )}
            </div>
            <div>
                <p className="mb-1 text-xs text-muted-foreground">Empty</p>
                <EditableValue canEdit onEdit={() => undefined} title="Click to edit" placeholder="Click to edit" data-testid="design-editable-value-empty" />
            </div>
        </div>
    )
}

const SECTIONS = [
    { id: 'overview', title: 'Overview' },
    { id: 'conventions', title: 'Conventions' },
    { id: 'page-header', title: 'Page header' },
    { id: 'color-tokens', title: 'Color tokens' },
    { id: 'thematic-palette', title: 'Thematic palette' },
    { id: 'gradients-tiles', title: 'Gradients & tiles' },
    { id: 'elevation', title: 'Elevation' },
    { id: 'buttons-badges', title: 'Buttons & badges' },
    { id: 'forms', title: 'Forms' },
    { id: 'data-display', title: 'Data display' },
    { id: 'feedback', title: 'Feedback' },
    { id: 'icons', title: 'Icon scale & colour' },
    { id: 'entity-icons', title: 'Entity icons' },
    { id: 'logomark', title: 'Logomark' },
    { id: 'archived-marker', title: 'Archived marker' },
    { id: 'tags', title: 'Tags' },
] as const

/** Display name and appearances of each entity icon, for the entity-icons block. */
const ENTITY_ICON_META: Record<(typeof ENTITY_ICON_KEYS)[number], { name: string; where: string }> = {
    assets: { name: 'Assets', where: 'Sidebar, page header, list row, link, search result' },
    conversations: { name: 'Conversations', where: 'Sidebar, page header, list row, search result' },
    contracts: { name: 'Contracts', where: 'Sidebar, page header, list row, link, badge' },
    contacts: { name: 'Contacts', where: 'Asset contacts, list row, link' },
    signatures: { name: 'Signatures', where: 'Sidebar, page header, badge' },
    capex: { name: 'Capex plan', where: 'Sidebar, page header, asset tab, command palette' },
    steering: { name: 'Steering', where: 'Sidebar, page header, home card' },
    group_steering: { name: 'Group steering', where: 'Sidebar, page header, home card' },
    benchmark: { name: 'Benchmark', where: 'Sidebar, page header' },
    documents: { name: 'Documents', where: 'Sidebar, page header, list row, link, badge' },
    knowledge: { name: 'Knowledge', where: 'Sidebar, page header, card' },
    campaigns: { name: 'Data collection campaigns', where: 'Sidebar, page header, empty state' },
    expenses: { name: 'Expenses', where: 'Sidebar, page header, list row, link' },
    interventions: { name: 'Interventions', where: 'Sidebar, page header, asset tab, list row' },
    intervention_reports: { name: 'Intervention reports', where: 'Intervention page, list row, link' },
    imports: { name: 'Imports', where: 'Sidebar, page header' },
    activity: { name: 'Activity', where: 'Sidebar, page header, asset tab, feed row' },
    properties: { name: 'Asset and document properties', where: 'Types registry, page header, card' },
}

/** The four elevation steps, in the order a surface stacks them. */
/** One shipped type per asset family, for the Gradients & tiles section. */
const ASSET_FAMILY_SAMPLES: { type: AssetTypeLike; note: string }[] = [
    {
        type: { code: 'building_group', kind: 'location', icon: 'home-group' },
        note: 'the site — its own colour, whatever else it is',
    },
    {
        type: { code: 'building', kind: 'location', icon: 'office-building' },
        note: 'built volume: buildings, floors, common areas',
    },
    {
        type: {
            code: 'unit',
            kind: 'location',
            icon: 'door-closed',
            properties: { private: true },
        },
        note: 'a private lot, and every room inside one',
    },
    {
        type: { code: 'outdoor', kind: 'location', icon: 'nature' },
        note: 'ground and outdoor: parcels, car parks, the site trade',
    },
    {
        type: { code: 'hvac_boiler', kind: 'equipment', icon: 'water-boiler' },
        note: 'trades, equipment leaves, structural items',
    },
]

const ELEVATION_STEPS = [
    { token: 'shadow-xs', className: 'shadow-xs', use: 'outline buttons, inputs' },
    { token: 'shadow-card', className: 'shadow-card', use: 'cards, KPI tiles, list containers' },
    { token: 'shadow-popover', className: 'shadow-popover', use: 'dropdowns, popovers, command palette' },
    { token: 'shadow-dialog', className: 'shadow-dialog', use: 'dialogs, sheets' },
] as const

/** `<pre>` + copy button. Every live example is followed by its import line. */
function CodeSnippet({ code }: { code: string }) {
    const [copied, setCopied] = useState(false)

    const copy = () => {
        void navigator.clipboard?.writeText(code)
        setCopied(true)
        window.setTimeout(() => setCopied(false), 1500)
    }

    return (
        <div className="relative mt-4 rounded-md border border-border bg-muted/40">
            <pre className="overflow-x-auto px-3 py-2 pr-12 font-mono text-[11px] leading-relaxed text-foreground">
                {code}
            </pre>
            <button
                type="button"
                onClick={copy}
                aria-label="Copy import"
                title="Copy import"
                data-testid="design-copy-btn"
                className="absolute right-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
                {copied ? <AppIcon path={mdiCheck} size="xs" /> : <AppIcon path={mdiContentCopy} size="xs" />}
            </button>
        </div>
    )
}

function DesignSection({
    id,
    title,
    rule,
    children,
}: {
    id: string
    title: string
    rule: string
    children: React.ReactNode
}) {
    return (
        <section
            id={id}
            data-testid={`design-section-${id}`}
            className="scroll-mt-28 border-b border-border py-10 first:pt-0 last:border-b-0"
        >
            <h2 className="mb-2 text-xl font-semibold text-foreground">{title}</h2>
            <p className="mb-6 max-w-3xl text-sm text-muted-foreground">{rule}</p>
            {children}
        </section>
    )
}

/** A labelled sub-block inside a section. */
function Demo({
    label,
    hint,
    children,
}: {
    label: string
    hint?: string
    children: React.ReactNode
}) {
    return (
        <div className="mb-6 last:mb-0">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {label}
            </p>
            {hint ? <p className="mb-3 text-sm text-muted-foreground">{hint}</p> : null}
            <div className="rounded-lg border border-border bg-background p-6">{children}</div>
        </div>
    )
}

// ---------------------------------------------------------------------------
// 2. Conventions
// ---------------------------------------------------------------------------

const CONVENTIONS: Array<{ rule: string; why: string }> = [
    {
        rule: 'No “← Back” button on an entity detail page.',
        why: 'The breadcrumb in the top bar is the canonical back affordance; a second one splits the user’s attention and drifts out of sync with the real parent. The only exception is a fatal-error / not-found shell, where the breadcrumb may point nowhere useful.',
    },
    {
        rule: 'Edit and Delete are always icon-only in a page header.',
        why: 'They are secondary controls. Giving them a label makes them compete visually with the page’s primary action, which is the one thing the header exists to offer.',
    },
    {
        rule: 'Header action buttons collapse to icon-only below `md`.',
        why: 'A phone header has room for the title or for four labels, not both. Hide the label with `hidden md:inline` and keep `aria-label` + `title` so the icon stays accessible and testable.',
    },
    {
        rule: 'Confirmations use `AlertDialog`, never `confirm()`.',
        why: 'The native dialog ignores the design system, cannot be styled, is unusable for a screen reader inside an SPA, and cannot be driven reliably by Playwright.',
    },
    {
        rule: 'Notifications are non-blocking. Never a toast.',
        why: 'A toast steals focus, expires before it is read, and stacks badly. State the outcome where the user is already looking — in the list, on the row, next to the field.',
    },
    {
        rule: 'The URL is the state for filters, sort and pagination.',
        why: 'A view a user can bookmark, share in a ticket and reload is worth more than a slightly shorter reducer. Every list change must be reflected in the router search params.',
    },
    {
        rule: '`data-testid` on every clickable element.',
        why: 'Text selectors break the moment the copy is translated — and this app ships in EN and FR. A test id is the only stable handle.',
    },
    {
        rule: 'Identifiers in English; only copy goes through i18n.',
        why: 'Routes, JSON fields, enum codes, i18n *keys* and test ids are machine-readable values and are English everywhere. French belongs in translation values only.',
    },
    {
        rule: 'People pickers search live through the API.',
        why: 'A contact, user or member picker sends what is typed as `q` (`UserSelect`, or `ApiSearchableSelect` plus chips for several). A list loaded once and filtered in the browser stops at its first page, and the person you need is on the next one.',
    },
    {
        rule: 'Writes are permission-gated with `<Can>` / `useCan`.',
        why: 'The API is the enforcer, but a button that always 403s is a broken promise. Gate the control on the same permission the route guard and the sidebar read.',
    },
]

// ---------------------------------------------------------------------------
// 4. Color tokens
// ---------------------------------------------------------------------------

const TOKEN_GROUPS: Array<{ group: string; tokens: string[] }> = [
    { group: 'Surfaces', tokens: ['--background', '--foreground', '--card', '--card-foreground', '--popover', '--popover-foreground'] },
    { group: 'Brand & primary', tokens: ['--brand', '--brand-foreground', '--primary', '--primary-foreground'] },
    { group: 'Neutrals', tokens: ['--secondary', '--secondary-foreground', '--muted', '--muted-foreground', '--accent', '--accent-foreground'] },
    { group: 'Semantic', tokens: ['--destructive', '--destructive-foreground', '--success', '--success-foreground', '--warning', '--warning-foreground', '--info', '--info-foreground'] },
    { group: 'Borders & inputs', tokens: ['--border', '--input', '--ring'] },
    { group: 'Charts', tokens: ['--chart-1', '--chart-2', '--chart-3', '--chart-4', '--chart-5'] },
    { group: 'Sidebar', tokens: ['--sidebar', '--sidebar-foreground', '--sidebar-primary', '--sidebar-primary-foreground', '--sidebar-accent', '--sidebar-accent-foreground', '--sidebar-border', '--sidebar-ring'] },
]

function Swatch({ token }: { token: string }) {
    return (
        <div className="flex items-center gap-2" data-testid={`design-token${token}`}>
            <span
                className="h-8 w-8 shrink-0 rounded-md border border-border"
                style={{ backgroundColor: `var(${token})` }}
            />
            <code className="truncate font-mono text-[11px] text-muted-foreground">{token}</code>
        </div>
    )
}

// ---------------------------------------------------------------------------
// 6. Forms — one assembled react-hook-form + Zod form
// ---------------------------------------------------------------------------

const demoSchema = z.object({
    code: z.string().min(2, 'At least 2 characters.').max(32),
    label: z.string().min(1, 'Required.'),
})

function DemoForm() {
    const form = useForm<z.infer<typeof demoSchema>>({
        resolver: zodResolver(demoSchema),
        mode: 'onChange',
        defaultValues: { code: '', label: '' },
    })
    const [saved, setSaved] = useState<string | null>(null)

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit((values) => setSaved(values.code))}
                className="max-w-md space-y-4"
            >
                <FormField
                    control={form.control}
                    name="code"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Code</FormLabel>
                            <FormControl>
                                <Input placeholder="BG001" data-testid="design-form-code" {...field} />
                            </FormControl>
                            <FormDescription>
                                Machine-readable, English, stable. Help text sits under the input.
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="label"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Label</FormLabel>
                            <FormControl>
                                <Input placeholder="Building group" data-testid="design-form-label" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <div className="flex items-center gap-2">
                    <Button
                        type="submit"
                        disabled={!form.formState.isValid}
                        data-testid="design-form-submit"
                    >
                        Save
                    </Button>
                    <Button type="button" variant="outline" data-testid="design-form-cancel">
                        Cancel
                    </Button>
                </div>
                {saved ? (
                    <p className="text-sm text-success" data-testid="design-form-saved">
                        Saved “{saved}”. Non-blocking, in place — never a toast.
                    </p>
                ) : null}
            </form>
        </Form>
    )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function DesignReferencePage() {
    const { org } = useParams({ from: '/$org/design-reference' })
    const { theme, setTheme } = useTheme()
    const [checked, setChecked] = useState(true)
    const [radio, setRadio] = useState('a')
    const [tab, setTab] = useState('overview')
    const [demoTags, setDemoTags] = useState<Tag[]>([])
    const [date, setDate] = useState<string | null>('2026-01-15')
    const [multilingual, setMultilingual] = useState<Record<string, string>>({
        en: 'Boiler room',
        fr: 'Chaufferie',
    })

    useBreadcrumbs([
        { title: 'Home', path: `/${org}` },
        { title: 'Design reference' },
    ])

    return (
        <div>
            <PageHeader
                icon={SECTION_ICONS.designReference}
                title="Design Reference"
                data-testid="design-reference-title"
                description="Every primitive, token and convention this app is built from — rendered live, in your current theme."
            />

            {/* Sticky anchor pills. A left rail would fight the sidebar. */}
            <nav
                aria-label="Design reference sections"
                className="sticky top-0 z-20 -mx-4 mb-8 mt-6 border-b border-border bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80"
                data-testid="design-reference-nav"
            >
                <ul className="flex flex-wrap gap-2">
                    {SECTIONS.map((s) => (
                        <li key={s.id}>
                            <a
                                href={`#${s.id}`}
                                className="inline-flex items-center rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                data-testid={`design-nav-${s.id}`}
                            >
                                {s.title}
                            </a>
                        </li>
                    ))}
                </ul>
            </nav>

            <main>
                {/* ---------------------------------------------------------- */}
                <DesignSection
                    id="overview"
                    title="Overview"
                    rule="This page is the answer to “what do we already have?”. Read it before you invent a component, a colour or a spacing."
                >
                    <div className="rounded-lg border border-border bg-background p-6 text-sm text-foreground">
                        <p className="mb-4">
                            <strong>design-reference = what exists.</strong> Tokens, primitives and
                            conventions: the raw material. <strong>patterns = how we compose it.</strong>{' '}
                            Production components fed static data, one snapshot per section. A composed
                            pattern never moves here; a primitive never gets a patterns section. The two
                            pages are siblings and both live inside <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">$org</code>{' '}
                            so theme, locale and breadcrumb behave exactly as on a real page.
                        </p>
                        <p className="mb-4">
                            Composition reference:{' '}
                            <Link
                                to="/$org/patterns"
                                params={{ org }}
                                className="text-primary underline-offset-4 hover:underline"
                                data-testid="design-patterns-link"
                            >
                                /{org}/patterns
                            </Link>
                            .
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm text-muted-foreground">Theme:</span>
                            {(['light', 'dark', 'system'] as const).map((value) => (
                                <Button
                                    key={value}
                                    size="sm"
                                    variant={theme === value ? 'default' : 'outline'}
                                    onClick={() => setTheme(value)}
                                    data-testid={`design-theme-${value}`}
                                >
                                    {value}
                                </Button>
                            ))}
                            <span className="text-xs text-muted-foreground">
                                Everything below is painted through <code className="font-mono">var(--…)</code>, so
                                flipping this proves parity.
                            </span>
                        </div>
                    </div>
                </DesignSection>

                {/* ---------------------------------------------------------- */}
                <DesignSection
                    id="conventions"
                    title="Conventions"
                    rule="The rules from front/CLAUDE.md “Core UI Principles”, each with the reason it exists — a rule whose why is forgotten is a rule that gets argued away."
                >
                    <div className="grid gap-3 md:grid-cols-2">
                        {CONVENTIONS.map((c) => (
                            <div
                                key={c.rule}
                                className="rounded-lg border border-border bg-background p-4"
                            >
                                <p className="text-sm font-medium text-foreground">{c.rule}</p>
                                <p className="mt-1.5 text-sm text-muted-foreground">{c.why}</p>
                            </div>
                        ))}
                    </div>
                </DesignSection>

                {/* ---------------------------------------------------------- */}
                <DesignSection
                    id="page-header"
                    title="Page header"
                    rule="One component, one H1 element, one type scale. The icon comes from lib/section-icons.ts, so a page's header icon is the sidebar icon of its section by construction, and it rides in an IconTile — the lg gradient tile, of which a view gets exactly one."
                >
                    <Demo label="List page" hint="Title plus the section's primary action.">
                        <PageHeader
                            icon={SECTION_ICONS.assets}
                            title="Assets"
                            actions={
                                <Button data-testid="design-header-primary">New asset</Button>
                            }
                        />
                    </Demo>

                    <Demo
                        label="Detail page"
                        hint="A ReactNode title (click-to-edit on real pages), a code chip in titleAside, icon-only Edit and Delete."
                    >
                        <PageHeader
                            icon={SECTION_ICONS.assets}
                            title="15 rue de la Paix"
                            titleAside={
                                <>
                                    <code className="rounded bg-muted px-2 py-1 font-mono text-xs text-muted-foreground">
                                        BG001
                                    </code>
                                    <Badge variant="success">Active</Badge>
                                </>
                            }
                            actions={
                                <>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        aria-label="Edit"
                                        title="Edit"
                                        data-testid="design-header-edit"
                                    >
                                        <AppIcon path={mdiPencil} size="sm" />
                                    </Button>
                                    <Button
                                        variant="destructive"
                                        size="icon"
                                        aria-label="Delete"
                                        title="Delete"
                                        data-testid="design-header-delete"
                                    >
                                        <AppIcon path={mdiDelete} size="sm" />
                                    </Button>
                                </>
                            }
                        />
                    </Demo>

                    <Demo label="Settings page" hint="Title plus description, no actions.">
                        <PageHeader
                            icon={SECTION_ICONS.settings}
                            title="Organization"
                            description="Name, locale, timezone and the identity this organization mails from."
                        />
                    </Demo>

                    <Demo
                        label="Gradient"
                        hint="The tile is brand on every page. The optional `gradient` prop exists for a header whose subject has an identity colour of its own; it takes the same names as IconTile (§ Icons)."
                    >
                        <div className="space-y-4">
                            <PageHeader
                                icon={SECTION_ICONS.assets}
                                title="Brand (the default)"
                                description="gradient is omitted — every routed page today looks like this."
                            />
                            <PageHeader
                                icon={SECTION_ICONS.expenses}
                                gradient="economic"
                                title="Economic"
                                description={'gradient="economic"'}
                            />
                        </div>
                    </Demo>

                    <Demo
                        label="Narrow width"
                        hint="Constrained to 340px: the title TRUNCATES rather than wrapping or pushing the action off the row. Below the md viewport breakpoint the title block additionally takes the whole row and the action bar wraps underneath it — resize the window to see that half, since it is a media query, not a container query."
                    >
                        <div className="w-[340px] max-w-full rounded-md border border-border p-3">
                            <PageHeader
                                icon={SECTION_ICONS.documents}
                                title="A deliberately very long page title that has to truncate"
                                actions={
                                    <Button data-testid="design-header-narrow-action">
                                        Upload
                                    </Button>
                                }
                            />
                        </div>
                    </Demo>

                    <Demo
                        label="Wrap (opt-in)"
                        hint="wrap lets the title WRAP instead of truncating, at the same 340px width as the demo above. Default everywhere else; the resident thread page (spec 2026-09-25-05) sets it because the title is the resident's own subject line and there is no action bar on that row to protect."
                    >
                        <div className="w-[340px] max-w-full rounded-md border border-border p-3">
                            <PageHeader
                                icon={SECTION_ICONS.conversations}
                                title="A deliberately very long page title that wraps instead"
                                wrap
                            />
                        </div>
                    </Demo>

                    <CodeSnippet
                        code={`import { PageHeader } from '@/components/shared/PageHeader'
import { SECTION_ICONS } from '@/lib/section-icons'

<PageHeader icon={SECTION_ICONS.assets} title="Assets" />
<PageHeader icon={SECTION_ICONS.expenses} gradient="economic" title="Expenses" />
<PageHeader icon={SECTION_ICONS.conversations} title={subject} wrap />`}
                    />
                </DesignSection>

                {/* ---------------------------------------------------------- */}
                <DesignSection
                    id="color-tokens"
                    title="Color tokens"
                    rule="Tokens only — never a hex value, never a Tailwind palette colour. Every swatch below is painted with var(--token), which is why the theme toggle in §1 proves light/dark parity in one click."
                >
                    {TOKEN_GROUPS.map((g) => (
                        <Demo key={g.group} label={g.group}>
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                                {g.tokens.map((token) => (
                                    <Swatch key={token} token={token} />
                                ))}
                            </div>
                        </Demo>
                    ))}
                    <CodeSnippet
                        code={`/* front/src/index.css declares them on :root and .dark */
className="bg-muted text-muted-foreground"   /* Tailwind maps to var(--muted) */
style={{ backgroundColor: 'var(--chart-1)' }} /* only where a class cannot */`}
                    />
                </DesignSection>

                {/* ---------------------------------------------------------- */}
                <DesignSection
                    id="thematic-palette"
                    title="Thematic palette"
                    rule="The categorical palette stored on enum values that have no colour yet, in the order they are handed out. Data colours, not theme tokens: a value keeps its colour on every plan. Mirrors internal/handlers/thematics/palette.go and migration 274."
                >
                    <div className="flex flex-wrap gap-2" data-testid="design-thematic-palette">
                        {['#4A8CF7', '#E8933A', '#2ECC91', '#9B72F7', '#F05252', '#F0B429', '#14B8A6', '#EC4899', '#84CC16', '#06B6D4', '#A16207', '#64748B'].map((c) => (
                            <span key={c} className="flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs">
                                <span className="inline-block h-3 w-3 rounded-sm" style={{ background: c }} />
                                <code>{c}</code>
                            </span>
                        ))}
                    </div>
                </DesignSection>

                {/* ---------------------------------------------------------- */}
                <DesignSection
                    id="gradients-tiles"
                    title="Gradients &amp; tiles"
                    rule="Colour by meaning, gradients on identity. A gradient tile marks WHAT A THING IS — a page, a KPI family, an asset type, the product. Data, controls and text stay flat: no gradient reaches a button, an input, a table or a data row. At most one lg tile per view; sm tiles may repeat in rows."
                >
                    <Demo
                        label="Gradient × size × tone"
                        hint="The full matrix: ten gradients, four sizes, two tones. solid fills with the gradient and paints the glyph white; soft tints with 10 % of the gradient's first stop and paints the glyph in that colour — for dense contexts where a saturated tile on every row would shout."
                    >
                        <div className="space-y-3">
                            <div className="flex items-center gap-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                                <span className="w-44 shrink-0" />
                                <span className="w-44">solid</span>
                                <span>soft</span>
                            </div>
                            {ICON_TILE_GRADIENTS.map((gradient: IconTileGradient) => (
                                <div
                                    key={gradient}
                                    className="flex items-center gap-4"
                                    data-testid={`design-tile-${gradient}`}
                                >
                                    <code className="w-44 shrink-0 font-mono text-[11px] text-muted-foreground">
                                        {gradient}
                                    </code>
                                    {(['solid', 'soft'] as const).map((tone) => (
                                        <div key={tone} className="flex w-44 items-center gap-2">
                                            {(Object.keys(ICON_TILE_SIZES) as IconTileSize[]).map(
                                                (size) => (
                                                    <IconTile
                                                        key={size}
                                                        path={SECTION_ICONS.assets}
                                                        gradient={gradient}
                                                        size={size}
                                                        tone={tone}
                                                    />
                                                )
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </Demo>

                    <Demo
                        label="The five asset families"
                        hint="An asset type is never drawn by hand: AssetTypeTile resolves the glyph from asset_types.icon and the colour from the family, so a tree of forty rows reads as sites / buildings / units / spaces / equipment. sm in rows, lg in a detail header."
                    >
                        <div className="space-y-3">
                            {ASSET_FAMILY_SAMPLES.map((sample) => (
                                <div
                                    key={sample.type.code}
                                    className="flex items-center gap-4"
                                    data-testid={`design-asset-family-${sample.type.code}`}
                                >
                                    <div className="flex w-24 shrink-0 items-center gap-3">
                                        <AssetTypeTile type={sample.type} size="sm" />
                                        <AssetTypeTile type={sample.type} size="lg" />
                                    </div>
                                    <code className="font-mono text-[11px] text-muted-foreground">
                                        {sample.type.code} → {assetTypeGradient(sample.type)}
                                    </code>
                                    <span className="text-xs text-muted-foreground">{sample.note}</span>
                                </div>
                            ))}
                        </div>
                    </Demo>

                    <CodeSnippet
                        code={`import { IconTile } from '@/components/ui/icon-tile'

<IconTile path={mdiHome} gradient="brand" size="lg" />
<IconTile path={mdiHome} gradient="economic" size="sm" tone="soft" />

/* An asset's own type — never IconTile directly. */
import { AssetTypeTile } from '@/components/assets/AssetTypeTile'

<AssetTypeTile type={assetType} size="sm" />
<AssetTypeTile type={assetType} asset={asset} size="lg" />`}
                    />
                </DesignSection>

                {/* ---------------------------------------------------------- */}
                <DesignSection
                    id="elevation"
                    title="Elevation"
                    rule="Soft shadows on a tinted canvas. Light: white surfaces lift off a faintly tinted page with a two-layer shadow. Dark: the page is slate, the card a visibly lighter surface, and the shadow gains an inset top highlight. Four steps, no others — reach for the token, never a raw Tailwind shadow."
                >
                    <Demo
                        label="The four shadows"
                        hint="Toggle the theme in §1: each one is tuned per theme, so both must read."
                    >
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            {ELEVATION_STEPS.map((step) => (
                                <div
                                    key={step.token}
                                    data-testid={`design-shadow-${step.token}`}
                                    className={`rounded-xl border border-border bg-card p-4 ${step.className}`}
                                >
                                    <code className="font-mono text-[11px] text-foreground">
                                        {step.token}
                                    </code>
                                    <p className="mt-1 text-xs text-muted-foreground">{step.use}</p>
                                </div>
                            ))}
                        </div>
                    </Demo>

                    <CodeSnippet
                        code={`/* --shadow-xs | --shadow-card | --shadow-popover | --shadow-dialog | --shadow-tile */
className="rounded-xl border bg-card shadow-card"`}
                    />
                </DesignSection>

                {/* ---------------------------------------------------------- */}
                <DesignSection
                    id="buttons-badges"
                    title="Buttons & badges"
                    rule="Every Button variant × size from ui/button.tsx, and every Badge variant from ui/badge.tsx. If what you need is not here, it does not exist — add it to the primitive, not to your page."
                >
                    <Demo label="Button variants">
                        <div className="flex flex-wrap items-center gap-2">
                            {(['default', 'secondary', 'outline', 'ghost', 'destructive', 'link'] as const).map(
                                (variant) => (
                                    <Button
                                        key={variant}
                                        variant={variant}
                                        data-testid={`design-btn-${variant}`}
                                    >
                                        {variant}
                                    </Button>
                                ),
                            )}
                        </div>
                    </Demo>

                    <Demo label="Button sizes" hint="sm · default · lg · icon, plus the disabled state.">
                        <div className="flex flex-wrap items-center gap-2">
                            <Button size="sm" data-testid="design-btn-sm">Small</Button>
                            <Button data-testid="design-btn-default">Default</Button>
                            <Button size="lg" data-testid="design-btn-lg">Large</Button>
                            <Button size="icon" aria-label="Icon button" title="Icon button" data-testid="design-btn-icon">
                                <AppIcon path={SECTION_ICONS.search} size="sm" />
                            </Button>
                            <Button disabled data-testid="design-btn-disabled">Disabled</Button>
                            <TooltipProvider>
                                <AddIconButton
                                    tooltip="Add (AddIconButton)"
                                    onClick={() => {}}
                                    testId="design-add-icon"
                                />
                            </TooltipProvider>
                        </div>
                    </Demo>

                    <Demo label="Badge variants">
                        <div className="flex flex-wrap items-center gap-2">
                            {(['default', 'secondary', 'outline', 'success', 'warning', 'info', 'destructive'] as const).map(
                                (variant) => (
                                    <Badge key={variant} variant={variant} data-testid={`design-badge-${variant}`}>
                                        {variant}
                                    </Badge>
                                ),
                            )}
                        </div>
                    </Demo>

                    <Demo
                        label="Attention badge"
                        hint="Red means something waits on you (a new or overdue ticket, an unread thread), never a total: a plain count stays a Badge. « 99+ » above 99; the dot replaces it on an icon-only sidebar, its meaning going to the button's aria-label."
                    >
                        <div className="flex flex-wrap items-center gap-3" data-testid="design-attention-badge">
                            <AttentionBadge count={3} aria-label="3 new tickets" />
                            <AttentionBadge count={42} aria-label="42 unread conversations" />
                            <AttentionBadge count={250} aria-label="250 documents to validate" />
                            <span className="relative inline-flex size-8 items-center justify-center rounded-md border">
                                <AppIcon path={SECTION_ICONS.steering} size="sm" />
                                <AttentionDot className="absolute right-0.5 top-0.5" />
                            </span>
                        </div>
                    </Demo>

                    <CodeSnippet
                        code={`import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { AddIconButton } from '@/components/ui/add-icon-button'
import { AttentionBadge, AttentionDot } from '@/components/ui/attention-badge'`}
                    />
                </DesignSection>

                {/* ---------------------------------------------------------- */}
                <DesignSection
                    id="forms"
                    title="Forms"
                    rule="The field primitives, then one assembled react-hook-form + Zod form showing the canonical label / input / help / error spacing and the “submit disabled until valid” rule."
                >
                    <Demo label="Field primitives">
                        <div className="max-w-md space-y-4">
                            <div className="space-y-1.5">
                                <Label htmlFor="design-input">Input</Label>
                                <Input id="design-input" placeholder="Placeholder" data-testid="design-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="design-textarea">Textarea</Label>
                                <Textarea id="design-textarea" placeholder="Longer text…" data-testid="design-textarea" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Select</Label>
                                <Select defaultValue="building">
                                    <SelectTrigger data-testid="design-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="building">Building</SelectItem>
                                        <SelectItem value="unit">Unit</SelectItem>
                                        <SelectItem value="room">Room</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="design-checkbox"
                                    checked={checked}
                                    onCheckedChange={(v) => setChecked(v === true)}
                                    data-testid="design-checkbox"
                                />
                                <Label htmlFor="design-checkbox">Checkbox</Label>
                            </div>
                            <RadioGroup value={radio} onValueChange={setRadio} className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <RadioGroupItem value="a" id="design-radio-a" data-testid="design-radio-a" />
                                    <Label htmlFor="design-radio-a">Radio A</Label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <RadioGroupItem value="b" id="design-radio-b" data-testid="design-radio-b" />
                                    <Label htmlFor="design-radio-b">Radio B</Label>
                                </div>
                            </RadioGroup>
                        </div>
                    </Demo>

                    <Demo label="FieldRow" hint="Label / value grid used on read-mostly detail panels.">
                        <div className="max-w-xl space-y-3">
                            <FieldRow label="External code" data-testid="design-field-row-code">
                                <Input defaultValue="0001-PA" data-testid="design-field-row-input" />
                            </FieldRow>
                            <FieldRow label="Surface" data-testid="design-field-row-surface">
                                <p className="pt-2 text-sm text-foreground">1 240 m²</p>
                            </FieldRow>
                        </div>
                    </Demo>

                    <Demo label="NullableDateField" hint="A date that is allowed to be empty, and can be cleared back to null.">
                        <div className="max-w-xs">
                            <NullableDateField
                                value={date}
                                onSave={async (next) => setDate(next)}
                                testIdPrefix="design-date"
                            />
                        </div>
                    </Demo>

                    <Demo
                        label="Editable value"
                        hint="An editable field rests as text (never a boxed input, select or combobox) with a hover background and a pointer cursor. One click on the value opens the editor, Enter and blur save, Escape cancels, an empty value shows “Click to edit”. Pickers rest as text too, open on one click and save on pick. No separate pencil for a single value; a card editing a set of linked entities keeps one card-level pencil that becomes Done."
                    >
                        <EditableValueDemo />
                    </Demo>

                    <Demo label="MultilingualInput" hint="One value per language behind tabs — the shape every user-facing label has.">
                        <div className="max-w-md">
                            <MultilingualInput
                                value={multilingual}
                                onChange={setMultilingual}
                                data-testid="design-multilingual"
                            />
                        </div>
                    </Demo>

                    <Demo
                        label="Assembled form"
                        hint="Label, control, help text, error message — and Save stays disabled until the schema is satisfied."
                    >
                        <DemoForm />
                    </Demo>

                    <CodeSnippet
                        code={`import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { FieldRow } from '@/components/ui/field-row'
import { NullableDateField } from '@/components/ui/NullableDateField'
import { EditableValue } from '@/components/ui/editable-value'
import { MultilingualInput } from '@/components/ui/multilingual-input'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'`}
                    />
                </DesignSection>

                {/* ---------------------------------------------------------- */}
                <DesignSection
                    id="data-display"
                    title="Data display"
                    rule="Table, Card, Tabs, Skeleton, Separator, ScrollArea — and the three list shells (loaded / loading / empty) side by side, because a list is not done until all three are."
                >
                    <Demo label="Table">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Code</TableHead>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                <TableRow data-testid="design-table-row">
                                    <TableCell className="font-mono text-xs">BG001</TableCell>
                                    <TableCell>15 rue de la Paix</TableCell>
                                    <TableCell><Badge variant="success">Active</Badge></TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className="font-mono text-xs">BG002</TableCell>
                                    <TableCell>8 avenue Foch</TableCell>
                                    <TableCell><Badge variant="warning">Draft</Badge></TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Demo>

                    <Demo label="Card">
                        <div className="grid gap-4 md:grid-cols-2">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Card</CardTitle>
                                    <CardDescription>Header, description, content.</CardDescription>
                                </CardHeader>
                                <CardContent className="text-sm text-muted-foreground">
                                    The default container for a bounded block of content.
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader>
                                    <CardDescription>Documents</CardDescription>
                                    <CardTitle className="text-3xl">1284</CardTitle>
                                </CardHeader>
                                <CardContent className="text-sm text-muted-foreground">
                                    +34 this week
                                </CardContent>
                            </Card>
                        </div>
                    </Demo>

                    <Demo label="Tabs">
                        <Tabs value={tab} onValueChange={setTab}>
                            <TabsList>
                                <TabsTrigger value="overview" data-testid="design-tab-overview">Overview</TabsTrigger>
                                <TabsTrigger value="properties" data-testid="design-tab-properties">Properties</TabsTrigger>
                            </TabsList>
                            <TabsContent value="overview" className="pt-4 text-sm text-muted-foreground">
                                Tabs address a sub-view of the same entity, and the active tab belongs in the URL.
                            </TabsContent>
                            <TabsContent value="properties" className="pt-4 text-sm text-muted-foreground">
                                Second panel.
                            </TabsContent>
                        </Tabs>
                    </Demo>

                    <Demo label="List shells" hint="Loaded, loading, empty — the three states every list owes its user.">
                        <div className="grid gap-4 md:grid-cols-3">
                            <div className="rounded-md border border-border p-4">
                                <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Loaded</p>
                                <ul className="space-y-2 text-sm">
                                    <li className="rounded-md border border-border px-3 py-2">15 rue de la Paix</li>
                                    <li className="rounded-md border border-border px-3 py-2">8 avenue Foch</li>
                                </ul>
                            </div>
                            <div className="rounded-md border border-border p-4" data-testid="design-list-loading">
                                <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Loading</p>
                                <div className="space-y-2">
                                    <Skeleton className="h-9 w-full" />
                                    <Skeleton className="h-9 w-full" />
                                </div>
                            </div>
                            <div className="rounded-md border border-border p-4" data-testid="design-list-empty">
                                <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Empty</p>
                                <EmptyState
                                    icon={mdiMagnify}
                                    live
                                    title="No asset matches this filter"
                                    description="Clear the filters to see the whole portfolio again."
                                    action={
                                        <Button size="sm" variant="outline" data-testid="design-empty-action">
                                            Clear filters
                                        </Button>
                                    }
                                />
                            </div>
                        </div>
                    </Demo>

                    <Demo
                        label="EmptyState — panel vs inline"
                        hint="panel is a page-level moment (xl icon, brand gradient); inline sits beside populated content and stays understated (sm icon, neutral gradient, spec 2026-09-24-01)."
                    >
                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="rounded-md border border-border p-4">
                                <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">panel</p>
                                <EmptyState
                                    icon={mdiMagnify}
                                    title="No asset matches this filter"
                                    description="Clear the filters to see the whole portfolio again."
                                />
                            </div>
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-base">Properties</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <EmptyState
                                        variant="inline"
                                        icon={mdiTag}
                                        title="No properties on this document"
                                        data-testid="design-empty-inline"
                                    />
                                </CardContent>
                            </Card>
                        </div>
                    </Demo>

                    <Demo label="Separator & ScrollArea">
                        <div className="space-y-4">
                            <Separator />
                            <ScrollArea className="h-28 w-full rounded-md border border-border p-3">
                                <div className="space-y-2 text-sm text-muted-foreground">
                                    {Array.from({ length: 12 }, (_, i) => (
                                        <p key={i}>Scrollable row {i + 1}</p>
                                    ))}
                                </div>
                            </ScrollArea>
                        </div>
                    </Demo>

                    <Demo
                        label="VoteBar and HelpHint — a split in one line, a reason one click away"
                        hint="VoteBar draws for / against / abstaining voices as one thin track and nothing at all without a voice. HelpHint folds an explanatory paragraph into a « ? » popover next to the one-line statement. Both come from the assembly screens (patterns 39 to 41)."
                    >
                        <div className="flex flex-wrap items-center gap-6" data-testid="design-vote-bar">
                            <VoteBar voicesFor={620} voicesAgainst={180} voicesAbstain={40} testId="design-vote-bar-demo" />
                            <p className="flex items-center gap-1 text-sm">
                                Mail votes close on March 8.
                                <HelpHint testId="design-help-hint">A form may be received until the third full day before the meeting.</HelpHint>
                            </p>
                        </div>
                    </Demo>

                    <CodeSnippet
                        code={`import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'`}
                    />
                </DesignSection>

                {/* ---------------------------------------------------------- */}
                <DesignSection
                    id="feedback"
                    title="Feedback & overlays"
                    rule="Alert, Dialog, AlertDialog, DeleteConfirmDialog, Tooltip, Popover, DropdownMenu, Sheet, Command. All live — open one and close it. And never a toast."
                >
                    <Demo label="Alert variants">
                        <div className="space-y-3">
                            <Alert data-testid="design-alert-default">
                                <AlertTitle>Heads up</AlertTitle>
                                <AlertDescription>
                                    The default alert states an outcome where the user is already looking.
                                </AlertDescription>
                            </Alert>
                            <Alert variant="warning" data-testid="design-alert-warning">
                                <AlertTitle>Something is degraded</AlertTitle>
                                <AlertDescription>
                                    Nothing was lost and nothing is being asked of you right now — a
                                    parked provider, a feature running without its optional half.
                                    Say since when, and when it will be checked again.
                                </AlertDescription>
                            </Alert>
                            <Alert variant="destructive" data-testid="design-alert-destructive">
                                <AlertTitle>Something failed</AlertTitle>
                                <AlertDescription>
                                    Say what failed and what to do about it — never just “Error”.
                                </AlertDescription>
                            </Alert>
                        </div>
                    </Demo>

                    <Demo label="Dialog & AlertDialog">
                        <div className="flex flex-wrap items-center gap-2">
                            <Dialog>
                                <DialogTrigger asChild>
                                    <Button variant="outline" data-testid="design-dialog-trigger">
                                        Open dialog
                                    </Button>
                                </DialogTrigger>
                                <DialogContent data-testid="design-dialog-content">
                                    <DialogHeader>
                                        <DialogTitle>Create an asset</DialogTitle>
                                        <DialogDescription>
                                            A Dialog carries a task. A confirmation is an AlertDialog instead.
                                        </DialogDescription>
                                    </DialogHeader>
                                    <DialogFooter>
                                        <Button data-testid="design-dialog-submit">Create</Button>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>

                            <AlertDialog>
                                <AlertDialogTrigger asChild>
                                    <Button variant="outline" data-testid="design-alertdialog-trigger">
                                        Open alert dialog
                                    </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent data-testid="design-alertdialog-content">
                                    <AlertDialogHeader>
                                        <AlertDialogTitle>Archive this asset?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                            AlertDialog replaces window.confirm() everywhere in this app.
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel data-testid="design-alertdialog-cancel">
                                            Cancel
                                        </AlertDialogCancel>
                                        <AlertDialogAction data-testid="design-alertdialog-confirm">
                                            Archive
                                        </AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>

                            <DeleteConfirmDialog
                                trigger={
                                    <Button variant="destructive" data-testid="design-delete-trigger">
                                        Delete
                                    </Button>
                                }
                                title="Delete this property definition?"
                                description="DeleteConfirmDialog is the pre-wired AlertDialog for destructive actions."
                                onConfirm={() => {}}
                            />
                        </div>
                    </Demo>

                    <Demo label="Tooltip, Popover, DropdownMenu">
                        <div className="flex flex-wrap items-center gap-2">
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button variant="outline" data-testid="design-tooltip-trigger">
                                            Hover me
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent data-testid="design-tooltip-content">
                                        A tooltip explains an icon; it never carries the only copy of a rule.
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>

                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" data-testid="design-popover-trigger">
                                        Open popover
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent data-testid="design-popover-content">
                                    <p className="text-sm text-muted-foreground">
                                        A popover holds a small piece of interactive UI — a filter, a picker.
                                    </p>
                                </PopoverContent>
                            </Popover>

                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" data-testid="design-dropdown-trigger">
                                        Open menu
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent data-testid="design-dropdown-content">
                                    <DropdownMenuLabel>Row actions</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem data-testid="design-dropdown-item">Edit</DropdownMenuItem>
                                    <DropdownMenuItem className="text-destructive focus:text-destructive">
                                        Delete
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>

                            <Sheet>
                                <SheetTrigger asChild>
                                    <Button variant="outline" data-testid="design-sheet-trigger">
                                        Open sheet
                                    </Button>
                                </SheetTrigger>
                                <SheetContent data-testid="design-sheet-content">
                                    <SheetHeader>
                                        <SheetTitle>Sheet</SheetTitle>
                                        <SheetDescription>
                                            The side panel — used for the mobile sidebar and for long secondary forms.
                                        </SheetDescription>
                                    </SheetHeader>
                                </SheetContent>
                            </Sheet>
                        </div>
                    </Demo>

                    <Demo label="Command" hint="The command palette primitive — also the body of every searchable picker.">
                        <div className="max-w-md rounded-md border border-border">
                            <Command>
                                <CommandInput placeholder="Search assets…" data-testid="design-command-input" />
                                <CommandList>
                                    <CommandEmpty>No result.</CommandEmpty>
                                    <CommandGroup heading="Assets">
                                        <CommandItem data-testid="design-command-item">15 rue de la Paix</CommandItem>
                                        <CommandItem>8 avenue Foch</CommandItem>
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </div>
                    </Demo>

                    <CodeSnippet
                        code={`import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { AlertDialog, /* … */ AlertDialogTrigger } from '@/components/ui/alert-dialog'
import { DeleteConfirmDialog } from '@/components/ui/delete-confirm-dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { DropdownMenu, /* … */ DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'`}
                    />
                </DesignSection>

                {/* ---------------------------------------------------------- */}
                <DesignSection
                    id="icons"
                    title="Icon scale &amp; colour"
                    rule="MDI only, and always through AppIcon — never @mdi/react directly (ESLint refuses the import). Sizes are names on a px grid: sm in a button or table cell, md in the sidebar and list rows, lg for a page-level action. A standalone icon is --icon blue; on a coloured surface it inherits. Pick a page's icon from the table below — do not import a path directly."
                >
                    <Demo
                        label="Scale × tone"
                        hint="The six named sizes, each in its three tones, beside a 13 px text line — the size a table cell or a list row prints at, which is what a glyph has to sit next to without looking small or shouty."
                    >
                        <div className="space-y-2">
                            <div className="flex items-center gap-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                                <span className="w-24 shrink-0" />
                                {(Object.keys(APP_ICON_TONE_CLASSES) as AppIconTone[]).map((tone) => (
                                    <span key={tone} className="w-52">
                                        {tone}
                                    </span>
                                ))}
                            </div>
                            {(Object.keys(APP_ICON_SIZES) as AppIconSize[]).map((size) => (
                                <div
                                    key={size}
                                    className="flex items-center gap-4"
                                    data-testid={`design-icon-size-${size}`}
                                >
                                    <code className="w-24 shrink-0 font-mono text-[11px] text-muted-foreground">
                                        {size} · {APP_ICON_SIZES[size]}px
                                    </code>
                                    {(Object.keys(APP_ICON_TONE_CLASSES) as AppIconTone[]).map(
                                        (tone) => (
                                            <span
                                                key={tone}
                                                className="flex w-52 items-center gap-2 text-[13px] text-foreground"
                                                data-testid={`design-icon-${size}-${tone}`}
                                            >
                                                <AppIcon
                                                    path={SECTION_ICONS.assets}
                                                    size={size}
                                                    tone={tone}
                                                />
                                                15 rue de la Paix
                                            </span>
                                        )
                                    )}
                                </div>
                            ))}
                        </div>
                    </Demo>

                    <Demo
                        label="Inheritance"
                        hint="None of these four icons carries a colour class. A coloured surface forces inheritance from the outside, so a call site never has to think about it."
                    >
                        <div className="flex flex-wrap items-center gap-3">
                            <Button variant="outline" size="sm" data-testid="design-icon-in-outline">
                                <AppIcon path={mdiPencil} size="sm" />
                                Outline
                            </Button>
                            <Button size="sm" data-testid="design-icon-in-primary">
                                <AppIcon path={mdiPencil} size="sm" />
                                Primary
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive"
                                data-testid="design-icon-in-destructive"
                            >
                                <AppIcon path={mdiDelete} size="sm" />
                                Delete
                            </Button>
                            <Badge variant="success" data-testid="design-icon-in-badge">
                                <AppIcon path={SECTION_ICONS.assets} size="xs" className="mr-1" />
                                Success
                            </Badge>
                        </div>
                    </Demo>

                    <Demo
                        label="lib/section-icons.ts"
                        hint="The whole table. Both AppSidebar and PageHeader read it, which is what makes a header icon and its nav entry agree."
                    >
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                            {(Object.keys(SECTION_ICONS) as SectionIconKey[]).map((key) => (
                                <div
                                    key={key}
                                    className="flex items-center gap-2 rounded-md border border-border px-2 py-1.5"
                                    data-testid={`design-icon-${key}`}
                                >
                                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-foreground">
                                        <AppIcon path={SECTION_ICONS[key]} size="sm" />
                                    </span>
                                    <code className="truncate font-mono text-[11px] text-muted-foreground">
                                        {key}
                                    </code>
                                </div>
                            ))}
                        </div>
                    </Demo>

                    <CodeSnippet
                        code={`import { AppIcon } from '@/components/ui/icon'
import { SECTION_ICONS } from '@/lib/section-icons'`}
                    />
                </DesignSection>

                {/* ---------------------------------------------------------- */}
                <DesignSection
                    id="entity-icons"
                    title="Entity icons"
                    rule="An entity is always drawn with its icon. Each entity below has exactly one glyph, read from SECTION_ICONS through AppIcon: in the sidebar, a page header, a list row, a cross-entity link, a badge, a card, an empty state, a search result or the command palette. No two entities share a glyph. Action icons (pencil, trash, plus) are not entities and stay out of this table."
                >
                    <Demo
                        label="The 18 entities"
                        hint="Icon, name, SECTION_ICONS key and where it shows up."
                    >
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                            {ENTITY_ICON_KEYS.map((key) => (
                                <div
                                    key={key}
                                    className="flex items-start gap-3 rounded-md border border-border px-3 py-2"
                                    data-testid={`entity-icon-${key}`}
                                >
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-foreground">
                                        <AppIcon path={SECTION_ICONS[key]} size="md" />
                                    </span>
                                    <span className="min-w-0">
                                        <span className="block text-sm font-medium">{ENTITY_ICON_META[key].name}</span>
                                        <code className="block truncate font-mono text-[11px] text-muted-foreground">
                                            {key}
                                        </code>
                                        <span className="block text-xs text-muted-foreground">
                                            {ENTITY_ICON_META[key].where}
                                        </span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </Demo>

                    <CodeSnippet
                        code={`<AppIcon path={SECTION_ICONS.contracts} size="sm" />  // a contract link, row or badge`}
                    />
                </DesignSection>

                {/* ---------------------------------------------------------- */}
                <DesignSection
                    id="logomark"
                    title="Logomark"
                    rule="A placeholder mark with a stable contract: a rounded square filled with --gradient-brand carrying three white slabs, a B that reads as floors. A commissioned logo replaces Logomark.tsx and public/logo-mark.svg without touching a call site. The gradient comes from the token, so the mark follows the theme."
                >
                    <Demo label="Sizes" hint="24, 32, 40 and 56 px, on the canvas and on a gradient.">
                        <div className="space-y-4">
                            <div className="flex items-end gap-6" data-testid="design-logomark-canvas">
                                {[24, 32, 40, 56].map((size) => (
                                    <div key={size} className="flex flex-col items-center gap-1">
                                        <Logomark size={size} />
                                        <code className="font-mono text-[11px] text-muted-foreground">
                                            {size}px
                                        </code>
                                    </div>
                                ))}
                            </div>
                            <div
                                className="flex items-end gap-6 rounded-lg bg-gradient-brand p-6"
                                data-testid="design-logomark-on-gradient"
                            >
                                {[24, 32, 40, 56].map((size) => (
                                    <Logomark key={size} size={size} />
                                ))}
                            </div>
                        </div>
                    </Demo>

                    <CodeSnippet
                        code={`import { Logomark } from '@/components/Logomark'

<Logomark size={32} label="Buildtory" />
/* pnpm icons:build regenerates the favicon set from public/logo-mark.svg */`}
                    />
                </DesignSection>

                {/* ---------------------------------------------------------- */}
                <DesignSection
                    id="archived-marker"
                    title="Archived marker"
                    rule="Anything that names an archived asset shows it struck through with the archive pictogram and the word Archived, so a link to an asset that is no longer live never looks like a live one. Strikethrough alone is easy to miss and the pictogram alone does not say 'no longer valid', so both are always used. A live asset renders as its bare name."
                >
                    <Demo label="Archived and live" hint="AssetName takes the asset's archived_at; ArchivedMarker alone renders nothing without it.">
                        <div className="space-y-3" data-testid="design-archived-marker">
                            <AssetName name="Pompes de relevage" archivedAt="2026-08-25T00:00:00Z" />
                            <div>
                                <AssetName name="Chaufferie" archivedAt={null} />
                            </div>
                            <div>
                                <ArchivedMarker archivedAt="2026-08-25T00:00:00Z" testId="design-archived-marker-alone" />
                            </div>
                        </div>
                    </Demo>

                    <CodeSnippet
                        code={`import { ArchivedMarker, AssetName } from '@/components/ArchivedMarker'

<AssetName name={asset.name} archivedAt={asset.archived_at} />
<ArchivedMarker archivedAt={asset.archived_at} />`}
                    />
                </DesignSection>

                {/* ---------------------------------------------------------- */}
                <DesignSection
                    id="tags"
                    title="Tags"
                    rule="Tags look and behave the same on every entity page: the badges (each with a remove cross), then the tag-plus trigger, always there for someone who may write. One click opens the picker to add, search or create a tag. There is no pencil and no Done button. A reader gets the badges only. Below md a header folds the list to a few tags and a +N chip."
                >
                    <div data-testid="design-tags" className="space-y-6">
                        <Demo label="Editable" hint="Badges with their remove cross, then the tag-plus trigger. The trigger opens the picker (search, create row).">
                            <div className="flex flex-wrap items-center gap-1" data-testid="design-tags-editable">
                                <TagBadgeLink org={org} tag={{ uid: 'demo-1', name: 'Heating', color: '#ef4444' }} onRemove={() => undefined} />
                                <TagBadgeLink org={org} tag={{ uid: 'demo-2', name: 'Safety', color: '#22c55e' }} onRemove={() => undefined} />
                                <TagPicker
                                    orgCode={org}
                                    entityType="asset"
                                    selectedTags={demoTags}
                                    onTagAdd={(tag) => setDemoTags((cur) => [...cur, tag])}
                                    onTagRemove={(tag) => setDemoTags((cur) => cur.filter((x) => x.uid !== tag.uid))}
                                    onTagCreate={() => undefined}
                                />
                            </div>
                        </Demo>
                        <Demo label="Folded" hint="collapse={{ below: 'md', max: 2 }}: only the first tags render, a +N chip expands the rest in place.">
                            <div className="flex flex-wrap items-center gap-1" data-testid="design-tags-folded">
                                <TagBadgeLink org={org} tag={{ uid: 'demo-1', name: 'Heating', color: '#ef4444' }} onRemove={() => undefined} />
                                <TagBadgeLink org={org} tag={{ uid: 'demo-2', name: 'Safety', color: '#22c55e' }} onRemove={() => undefined} />
                                <button
                                    type="button"
                                    className={cn(badgeVariants({ variant: 'outline' }), 'cursor-pointer whitespace-nowrap hover:bg-muted')}
                                    aria-label="+2"
                                    data-testid="design-tags-more"
                                >
                                    +2
                                </button>
                            </div>
                        </Demo>
                        <Demo label="Read-only" hint="No write permission: badges only, no trigger, no crosses.">
                            <div className="flex flex-wrap items-center gap-1" data-testid="design-tags-readonly">
                                <TagBadgeLink org={org} tag={{ uid: 'demo-1', name: 'Heating', color: '#ef4444' }} />
                                <TagBadgeLink org={org} tag={{ uid: 'demo-2', name: 'Safety', color: '#22c55e' }} />
                            </div>
                        </Demo>
                    </div>

                    <CodeSnippet
                        code={`<EntityTagsSection
  org={org}
  entityType="contract"
  entityUid={contract.uid}
  collapse={{ below: 'md', max: 2 }}
  editable={useCan('contracts.write')}
/>`}
                    />
                </DesignSection>
            </main>
        </div>
    )
}

export const Route = createFileRoute('/$org/design-reference')({
    component: DesignReferencePage,
})
